import _, { isNil, get } from 'lodash';
import { shouldTruncateString, truncateString } from './truncate-string-util';
import { isNumeric } from '../../../../shared-modules/data/utils/validation-utils';
import numeral from 'numeral';
import timeFlexibleEndings from '@enums/time-flexible-endings';

import { getTsgKeyFromColId } from './tsg-utils';

const valueIsFalsy = ({ value }) => {
  return _.isNil(value) || value.toString().trim() === '';
};

/* All validations here return true in case value didn't pass validation,
 ** so that we don't need to negate checks everywhere. For example,
 ** validateValueExists will return false if value exists and true if not
 */

const validateValueExists = params => {
  const { value, colDef } = params;
  return colDef.required && colDef.editable && valueIsFalsy({ value });
};

const validations = {
  valueIsFalsy,
  validateValueExists,
  valueIsNotNegative: ({ value }) => {
    return !_.isNumber(value) || _.toNumber(value) < 0;
  },
  valueIsNotNumeric: ({ value }) => !isNumeric(value),
};

/**
 * Default parser for ag-grid table cell values
 * @param params - ValueParserParams object from ag-grid that contains the new value of the cell
 * @returns {string} - Processed string
 */
const defaultParser = params => {
  if (!params.newValue) return;
  // trim the new value so that no odd spaces are saved
  const inputValue = params.newValue;

  return inputValue.toString().trim();
};

const getNumericParser = stringToNumberFormatter => {
  /**
   * Numeric parser for ag-grid table cell values
   * @param {object} params - Params object from ag-grid that contains the new value of the cell
   * @returns {(number | string)} - ag-grid returns strings by default,
   * if a value is castable to a number, return that value, otherwise, return whatever was passed
   */
  return params => {
    const inputValue = defaultParser(params);
    if (['', undefined].includes(inputValue)) return '';

    // needs to be symmetric with formatNumber
    const valueAfterCoercion = stringToNumberFormatter(inputValue);
    return !_.isNaN(valueAfterCoercion) ? valueAfterCoercion : inputValue;
  };
};

const getNumberFormatter = ({ formatter, formatterParams, divideBy }) => {
  return params => {
    const value = _.get(params.data, params.colDef.field);
    const mappedValue = divideBy ? value / divideBy : value;
    return _.isFinite(mappedValue)
      ? formatter({ number: mappedValue, ...formatterParams })
      : mappedValue || '-';
  };
};

const getNumericColumnCustomType = ({
  formatter,
  formatterParams,
  stringToNumberFormatter,
  divideBy,
}) => ({
  valueParser: getNumericParser(stringToNumberFormatter),
  // required to show localized number when editing cell
  valueGetter: getNumberFormatter({ formatter, formatterParams, divideBy }),
  valueFormatter: getNumberFormatter({ formatter, formatterParams, divideBy }),
});

const getTruncatedValueGetters = ({ truncationLength, ignoreRegistry }) => ({
  valueGetter: params => {
    const value = _.get(params.data, params.colDef.field);
    const { wasTruncated, truncatedValue } = truncateString({
      text: value,
      truncationLength,
      ignoreRegistry,
    });
    return wasTruncated ? truncatedValue : value;
  },
  tooltipValueGetter: params => {
    const value = _.get(params.data, params.colDef.field);
    const { shouldTruncate } = shouldTruncateString({
      text: value,
      truncationLength,
      ignoreRegistry,
    });
    return shouldTruncate ? value : null;
  },
});

const checkboxRenderer = (params, eventHandlerMethod) => {
  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  checkbox.disabled = _.get(params, 'disabled');

  const pathForBox = params.colDef.field;
  const valueForBox = _.get(params.data, pathForBox);
  checkbox.checked = valueForBox;
  checkbox.addEventListener('change', () => eventHandlerMethod(params));
  return checkbox;
};

/**
 * Creates a timestamp comparator function for ag-grid columns
 * @param {string} timestampPath - The path to the timestamp property in the node data
 * @returns {function} A comparator function for ag-grid sorting
 */
const createTimestampComparator = timestampPath => {
  return (valueA, valueB, nodeA, nodeB, isInverted) => {
    // Get timestamp values from the data
    const timestampA = _.get(nodeA.data, timestampPath);
    const timestampB = _.get(nodeB.data, timestampPath);

    // Handle null/undefined timestamps - always put them at the end regardless of sort direction.
    // When isInverted (desc), AG Grid negates the return value, so we flip the sentinels to
    // counteract the negation and keep nulls at the bottom.
    if (!timestampA && !timestampB) return 0;
    if (!timestampA) return isInverted ? -1 : 1;
    if (!timestampB) return isInverted ? 1 : -1;

    // Compare timestamps directly (they're numeric values)
    return timestampA - timestampB;
  };
};

const utils = {
  checkboxRenderer,
  createTimestampComparator,
};

const getStrictWidthColumn = width => ({
  minWidth: width,
  maxWidth: width,
});

const fmtDecimalOptionalMantissa = '0[.][00]';

/**
 * Creates filter parameters for numeric columns with locale support and optional negative values
 * @param {Object} options - Configuration options
 * @param {boolean} options.allowNegative - Whether to allow negative values in filter
 * @returns {Object} - AG Grid filter parameters
 */
function getNumericFilterParams({ allowNegative = true } = {}) {
  return {
    // Allow digits, decimal point, comma, and optional negative sign
    allowedCharPattern: `\\d\\.,${allowNegative ? '\\-' : ''}`,

    // Format the filter value display
    numberFormatter: val => {
      if (isNil(val)) return '';
      return numeral(val).format(fmtDecimalOptionalMantissa);
    },

    // Parse input with locale support
    numberParser: inputString => {
      // Handle empty values by returning null instead of 0
      // This prevents filters from applying with empty values
      if (inputString === '' || isNil(inputString)) return null;

      // Parse to number with locale awareness
      return numeral(inputString).value();
    },
  };
}

/**
 * Creates filter parameters for numeric columns using agMultiColumnFilter,
 * combining agSetColumnFilter (value-selection list) with agNumberColumnFilter (range/comparison operators).
 * @param {Object} options - Configuration options
 * @param {boolean} options.allowNegative - Whether to allow negative values in the number filter
 * @returns {Object} - AG Grid multi-column filter parameters
 */
function getMultiNumericFilterParams({ allowNegative = true } = {}) {
  return {
    filters: [
      { filter: 'agSetColumnFilter' },
      {
        filter: 'agNumberColumnFilter',
        filterParams: getNumericFilterParams({ allowNegative }),
      },
    ],
  };
}

/**
 * Returns a filterValueGetter that exposes '—' as a filterable option in agSetColumnFilter
 * when the field value matches a reference field value (the same condition that causes the
 * valueFormatter to display a dash instead of a price).
 * @param {string} fieldPath - Dot-notation path to the field being filtered
 * @param {string} refFieldPath - Dot-notation path to the reference field to compare against
 * @returns {Function} AG Grid filterValueGetter
 */
function getDashWhenEqualToRefFilterValueGetter(fieldPath, refFieldPath) {
  return params => {
    const value = get(params.data, fieldPath);
    const refValue = get(params.data, refFieldPath);
    return value === refValue ? '—' : value;
  };
}

/**
 * Refreshes specific cells or an entire row in the grid
 * @param {Object} gridApi - The AG Grid API instance
 * @param {Object} rowNode - Row node reference to refresh
 * @param {Array|String} [columns] - Optional column ID(s) to refresh specific cells
 */
const refreshGridCells = (gridApi, rowNode, columns = null) => {
  if (!gridApi || !rowNode) {
    return;
  }

  const refreshParams = {
    rowNodes: [rowNode],
    force: true,
  };

  // If columns specified, only refresh those specific cells
  if (columns) {
    refreshParams.columns = _.isArray(columns) ? columns : [columns];
  }

  // Refresh the cells
  gridApi.refreshCells(refreshParams);
};

/**
 * Generates time-flexible field names based on historical period
 * @param {string} fieldName - Base field name (e.g., 'yearlySales', 'yearlyVolume')
 * @param {string|null} historicalPeriod - Historical period or null for all endings
 * @returns {Array} - Array of field names to include
 */
const generateTimeFlexibleFieldNames = (fieldName, historicalPeriod) => {
  // Extract base field name (remove 'yearly' prefix)
  const baseFieldName = fieldName.replace(/^yearly/, '');

  if (historicalPeriod) {
    // If the prefix is 'yearly', return the yearly field only
    if (historicalPeriod === 'yearly') {
      // e.g., for 'yearlySales' => ['yearlySales']
      return [fieldName];
    }
    // Otherwise, return the time-flexible field
    // e.g., for 'yearlySales' and '13WeeksLookBack' => ['sales13WeeksLookBack']
    return [`${baseFieldName.toLowerCase()}${historicalPeriod}`];
  }
  // Return all time-flexible endings
  return _.keys(timeFlexibleEndings).map(ending => `${baseFieldName.toLowerCase()}${ending}`);
};

/**
 * Field dependency mappings for the frontend to control what data is requested
 */
const fieldDependencies = {
  /**
   * Fields required when scenarioPrice is requested
   */
  scenarioPrice: [
    // IDs
    '_id',
    'scenarioKey',
    'workpackageId',
    'productKey',
    'productKeyDisplay',
    'toolStoreGroupKey',

    // KPI / Engine Input
    'liveCost.price',
    'livePrice.price',
    'intentionPrice.price',
    // Required for price toggle conversions
    'contentValue',
    'normWeight',

    // Price override related
    'hasUnsavedKpiResults',
    'overridePrice',
    'hasPriceChanged',

    // Promotion Flags
    'hasUpcomingPromotion',
    'isOnPromotion',

    // Scenario Price and Related
    'optimizedPrice',
    // for price toggle functionality
    'scenarioPricePerNormWeight',
    'scenarioPricePerContentUnit',
    // for price change toggle functionality (delta columns)
    'scenarioPriceChange',
    'scenarioPriceChangeRatio',

    // Cost delta scaling: baseline and go-live costs for CostPriceDelta ratio for Bulk Actions and price override logic
    'referenceDateCost',
    'intentionCost.price',

    // Baseline costs for TransferPriceDelta and PurchasePriceDelta ratios for Bulk Actions and price override logic
    'referenceDateTransferPrice.price',
    'referenceDatePurchasePrice.price',

    // Reference Prices for Bulk actions and price override logic
    'intentionTransferPrice.price',
    'liveTransferPrice.price',
    'livePurchasePrice.price',
    'intentionPurchasePrice.price',

    // Other
    'transitionPeriodOverrideMetadata',
  ],

  'liveCost.price': ['liveCost.pricePerContentUnit', 'liveCost.pricePerNormWeight'],
  nonPromoNetCost: ['nonPromoNetCostPerContentUnit', 'nonPromoNetCostPerNormWeight'],
  costDelta: ['costDeltaPerContentUnit', 'costDeltaPerNormWeight'],
  costDeltaPercent: [
    'costDelta',
    'costDeltaPerContentUnit',
    'costDeltaPerNormWeight',
    'nonPromoNetCost',
    'nonPromoNetCostPerContentUnit',
    'nonPromoNetCostPerNormWeight',
  ],
  'liveTransferPrice.price': [
    'liveTransferPrice.pricePerContentUnit',
    'liveTransferPrice.pricePerNormWeight',
  ],
  'intentionTransferPrice.price': [
    'intentionTransferPrice.pricePerContentUnit',
    'intentionTransferPrice.pricePerNormWeight',
  ],
  'livePurchasePrice.price': [
    'livePurchasePrice.pricePerContentUnit',
    'livePurchasePrice.pricePerNormWeight',
  ],
  'intentionPurchasePrice.price': [
    'intentionPurchasePrice.pricePerContentUnit',
    'intentionPurchasePrice.pricePerNormWeight',
  ],
  transferPriceDelta: ['transferPriceDeltaPerContentUnit', 'transferPriceDeltaPerNormWeight'],
  purchasePriceDelta: ['purchasePriceDeltaPerContentUnit', 'purchasePriceDeltaPerNormWeight'],
  weekPriceChange: ['priceHistoryWeek', 'intentionPrice.price'],
  'livePrice.price': ['livePrice.pricePerContentUnit', 'livePrice.pricePerNormWeight'],
  'intentionPrice.price': [
    'intentionPrice.pricePerContentUnit',
    'intentionPrice.pricePerNormWeight',
    'livePrice.price',
    'livePrice.pricePerContentUnit',
    'livePrice.pricePerNormWeight',
    'effectiveDate',
  ],
  'overridePrice.referencePriceAtOverride': [
    'contentValue',
    'normWeight',
    'hasUnsavedKpiResults',
    'overridePrice.price',
    'scenarioPrice',
    'overridePrice.expiryDate',
    'currentPromotion',
    'upcomingPromotion',
    'childPromotions.currentPromotion',
    'childPromotions.upcomingPromotion',
    'lastEngineSource',
    'optimizedPrice',
    'crossElasticityPrice',
    'strategicPrice',
  ],
  effectiveDateRange: [
    // IDs
    '_id',
    'scenarioKey',
    'workpackageId',
    'productKey',
    'productKeyDisplay',
    'toolStoreGroupKey',

    // KPI / Engine Input
    'liveCost.price',
    'livePrice.price',
    'intentionPrice.price',
    'hasUnsavedKpiResults',
    'inferredGoLiveDate',
    'intentionPrice.effectiveDate',
    'inferredGoLivePrice',

    // Promotion Flags
    'hasUpcomingPromotion',
    'isOnPromotion',
    'currentPromotion',
    'upcomingPromotion',
    'childPromotions.currentPromotion',
    'childPromotions.upcomingPromotion',

    // Scenario Price and Related
    'optimizedPrice',

    // Other
    'overridePrice',
    'transitionPeriodOverrideMetadata',
  ],
  scenarioPriceChange: ['scenarioPriceChangePerNormWeight', 'scenarioPriceChangePerContentUnit'],
  scenarioPriceChangeRatio: [
    'scenarioPriceChangeRatioPerNormWeight',
    'scenarioPriceChangeRatioPerContentUnit',
  ],
  scenarioSalesImpact: ['regularSalesImpact', 'scenarioSalesImpact'],
  scenarioCostImpact: ['regularCostImpact', 'scenarioCostImpact'],
  /**
   * Fields required when tension is requested
   */
  tension: [
    'scenarioPrice',
    'economicReferencePrice',
    'competitorReferencePrice',
    'architectureReferencePrice',
    'storeGroupReferencePrice',
    'minMaxReferencePrice',
  ],

  /**
   * Fields required when filteredAlerts is requested
   */
  filteredAlerts: ['alerts', 'alertCounts'],
  'mandatoryEngineInputs.promoParticipationSales': [
    'mandatoryEngineInputs.promoDiscount',
    'mandatoryEngineInputs.promoParticipationSales',
    'mandatoryEngineInputs.promoFunding',
  ],
  'distributionByAssortmentRole.changingAssortment': ['distributionByAssortmentRole'],
};

const extendReleaseFlaggedDependencies = (dependencies, releaseFlags = {}) => {
  const isNewPriceOverrideAPIEnabled = get(releaseFlags, 'enableNewPriceOverrideAPI');

  if (!isNewPriceOverrideAPIEnabled) {
    dependencies = {
      ...dependencies,
      scenarioPrice: [
        ...get(dependencies, 'scenarioPrice', []),

        // KPI / Engine Input
        'yearlyVolume',
        'mandatoryEngineInputs.forecastVolume',
        'mandatoryEngineInputs.nonPromoNetCost',
        'mandatoryEngineInputs.promoParticipationSales',
        'mandatoryEngineInputs.promoDiscount',
        'mandatoryEngineInputs.promoFunding',
        'vatRate',
        'vatCalc',

        // Scenario Price and Related
        'scenarioSalesImpact',
        'regularSalesImpact',
        'scenarioCostImpact',
        'scenarioMargin',
        'regularCostImpact',
        'crossElasticityVolume',
        'crossElasticityPrice',
        'strategicForecastVolume',
        'strategicPrice',
        'staticForecastVolume',

        // Reference Prices
        'economicReferencePrice',
        'architectureReferencePrice',
        'competitorReferencePrice',

        // Penalties
        'competitorPenalty',
        'economicPenalty',
        'architecturePenalty',

        // Other
        'alerts',
        'priceRange',
        'similarityGroupKey',
        'cannGroupId',
      ],
      effectiveDateRange: [
        ...get(dependencies, 'effectiveDateRange', []),

        // KPI / Engine Input
        'yearlyVolume',
        'mandatoryEngineInputs.forecastVolume',
        'mandatoryEngineInputs.nonPromoNetCost',
        'mandatoryEngineInputs.promoParticipationSales',
        'mandatoryEngineInputs.promoDiscount',
        'mandatoryEngineInputs.promoFunding',
        'vatRate',
        'vatCalc',

        // Scenario Price and Related
        'scenarioPricePerNormWeight',
        'scenarioPricePerContentUnit',
        'scenarioPriceChange',
        'scenarioPriceChangeRatio',
        'scenarioSalesImpact',
        'regularSalesImpact',
        'scenarioCostImpact',
        'scenarioMargin',
        'regularCostImpact',
        'crossElasticityVolume',
        'crossElasticityPrice',
        'strategicForecastVolume',
        'strategicPrice',
        'staticForecastVolume',

        // Reference Prices
        'economicReferencePrice',
        'architectureReferencePrice',
        'competitorReferencePrice',

        // Penalties
        'competitorPenalty',
        'economicPenalty',
        'architecturePenalty',

        // Other
        'alerts',
        'priceRange',
        'similarityGroupKey',
        'cannGroupId',
      ],
    };
  }
  return dependencies;
};

/**
 * Expands field requests to include dependencies
 * @param {Array} requestedFields - Array of field names requested
 * @param {Object} dependencies - Dependency mapping object
 * @returns {Array} - Expanded array of field names including dependencies
 */
const expandFieldDependencies = (requestedFields, dependencies = fieldDependencies) => {
  if (!requestedFields || !Array.isArray(requestedFields)) {
    return requestedFields;
  }

  const expandedFields = new Set(requestedFields);

  // Check each requested field for dependencies
  requestedFields.forEach(field => {
    if (dependencies[field]) {
      dependencies[field].forEach(dep => {
        expandedFields.add(dep);
      });
    }
  });

  return Array.from(expandedFields);
};

/**
 * Expands time-flexible fields based on historical period
 * @param {Array} requestedFields - Array of field names requested
 * @param {string|null} historicalPeriod - Historical period for time-flexible fields
 * @returns {Array} - Expanded array of field names including time-flexible variants
 */
const expandTimeFlexibleFields = (requestedFields, historicalPeriod = null) => {
  if (!requestedFields || !Array.isArray(requestedFields)) {
    return requestedFields;
  }

  const expandedFields = new Set(requestedFields);

  // Check each requested field for time-flexible variants
  requestedFields.forEach(field => {
    // Check if field is a time-flexible field (starts with 'yearly')
    if (field.startsWith('yearly')) {
      const timeFlexibleVariants = generateTimeFlexibleFieldNames(field, historicalPeriod);
      timeFlexibleVariants.forEach(variant => {
        expandedFields.add(variant);
      });
    }
  });

  return Array.from(expandedFields);
};

/**
 * Expands pick configuration to include all field dependencies and time-flexible fields
 * @param {Object} pickConfig - Pick configuration object
 * @param {Object} dependencies - Dependency mapping object
 * @param {string|null} historicalPeriod - Historical period for time-flexible fields
 * @returns {Object} - Expanded pick configuration
 */
const expandPickConfigDependencies = (
  pickConfig,
  dependencies = fieldDependencies,
  historicalPeriod = null
) => {
  if (!pickConfig) return pickConfig;

  const expanded = { ...pickConfig };

  // Expand simpleFields
  if (expanded.simpleFields) {
    expanded.simpleFields = expandFieldDependencies(expanded.simpleFields, dependencies);
    expanded.simpleFields = expandTimeFlexibleFields(expanded.simpleFields, historicalPeriod);
  }

  // Expand toolStoreGroupFields
  if (expanded.toolStoreGroupFields) {
    Object.keys(expanded.toolStoreGroupFields).forEach(tsgKey => {
      expanded.toolStoreGroupFields[tsgKey] = expandFieldDependencies(
        expanded.toolStoreGroupFields[tsgKey],
        dependencies
      );
      expanded.toolStoreGroupFields[tsgKey] = expandTimeFlexibleFields(
        expanded.toolStoreGroupFields[tsgKey],
        historicalPeriod
      );
    });
  }

  return expanded;
};

/**
 * Processes tool store group competitor fields
 * @param {string} fieldPath - Field path to process
 * @param {Object} pickConfig - Pick configuration object
 * @returns {boolean} - True if field was processed as TSG competitor
 */
const processToolStoreGroupCompetitorField = (fieldPath, pickConfig) => {
  const tsgCompetitorMatch = fieldPath.match(
    /^toolStoreGroups\.([^.]+)\.competitors\.([^.]+)\.(.+)$/
  );
  if (tsgCompetitorMatch) {
    const [, tsgKey, competitorKey] = tsgCompetitorMatch;
    if (!pickConfig.tsgCompetitors[tsgKey]) {
      pickConfig.tsgCompetitors[tsgKey] = [];
    }
    if (!pickConfig.tsgCompetitors[tsgKey].includes(competitorKey)) {
      pickConfig.tsgCompetitors[tsgKey].push(competitorKey);
    }
    return true;
  }
  return false;
};

/**
 * Processes tool store group fields
 * @param {string} fieldPath - Field path to process
 * @param {Object} pickConfig - Pick configuration object
 * @returns {boolean} - True if field was processed as TSG field
 */
const processToolStoreGroupField = (fieldPath, pickConfig) => {
  const tsgMatch = fieldPath.match(/^toolStoreGroups\.([^.]+)\.(.+)$/);
  if (tsgMatch) {
    const [, tsgKey, field] = tsgMatch;
    if (!pickConfig.toolStoreGroupFields[tsgKey]) {
      pickConfig.toolStoreGroupFields[tsgKey] = [];
    }
    pickConfig.toolStoreGroupFields[tsgKey].push(field);
    return true;
  }
  return false;
};

/**
 * Processes competitor fields
 * @param {string} fieldPath - Field path to process
 * @param {Object} pickConfig - Pick configuration object
 * @returns {boolean} - True if field was processed as competitor field
 */
const processCompetitorField = (fieldPath, pickConfig) => {
  const competitorMatch = fieldPath.match(/^competitors\.([^.]+)\.(.+)$/);
  if (competitorMatch) {
    const [, competitorKey] = competitorMatch;
    if (!pickConfig.competitors.includes(competitorKey)) {
      pickConfig.competitors.push(competitorKey);
    }
    return true;
  }
  return false;
};

/**
 * Processes hierarchy fields
 * @param {string} fieldPath - Field path to process
 * @param {Object} pickConfig - Pick configuration object
 * @returns {boolean} - True if field was processed as hierarchy field
 */
const processHierarchyField = (fieldPath, pickConfig) => {
  const hierarchyMatch = fieldPath.match(/^hierarchy\.(\d+)\.(.+)$/);
  if (hierarchyMatch) {
    const [, level] = hierarchyMatch;
    const hierarchyLevelPath = `hierarchy.${level}`;
    if (!pickConfig.simpleFields.includes(hierarchyLevelPath)) {
      pickConfig.simpleFields.push(hierarchyLevelPath);
    }
    return true;
  }
  return false;
};

/**
 * Processes attribute fields
 * @param {string} fieldPath - Field path to process
 * @param {Object} pickConfig - Pick configuration object
 * @returns {boolean} - True if field was processed as attribute field
 */
const processAttributeField = (fieldPath, pickConfig) => {
  if (fieldPath.startsWith('attributes.')) {
    pickConfig.simpleFields.push(fieldPath);
    return true;
  }
  return false;
};

/**
 * Extracts field paths from AG Grid column state for use with backend pick parameter
 * Now includes dependency expansion and time-flexible field expansion
 * @param {Array} columnState - AG Grid column state array with objects containing colId, hide, etc.
 * @param {Object} dependencies - Optional dependency mapping object
 * @param {string|null} historicalPeriod - Historical period for time-flexible fields
 * @returns {Object} Structured pick configuration for backend with expanded dependencies
 */
const extractPickFieldsFromColumnState = (
  columnState,
  dependencies = fieldDependencies,
  historicalPeriod = 'yearly',
  releaseFlags = {}
) => {
  if (!columnState || !Array.isArray(columnState)) {
    return null;
  }

  dependencies = extendReleaseFlaggedDependencies(dependencies, releaseFlags);

  // Extract colId from each column, filtering out hidden columns
  const visibleColumns = columnState.filter(col => !col.hide);
  const fieldPaths = visibleColumns.map(col => col.colId);

  // Group fields by type for efficient backend processing
  const pickConfig = {
    simpleFields: [],
    toolStoreGroupFields: {}, // toolStoreGroupKey -> array of fields
    tsgCompetitors: {}, // toolStoreGroupKey -> array of competitor keys
    competitors: [], // array of competitor keys (for useStoreGroupChains false)
  };

  fieldPaths.forEach(fieldPath => {
    // Try each processor in order of specificity
    if (processToolStoreGroupCompetitorField(fieldPath, pickConfig)) return;
    if (processToolStoreGroupField(fieldPath, pickConfig)) return;
    if (processCompetitorField(fieldPath, pickConfig)) return;
    if (processHierarchyField(fieldPath, pickConfig)) return;
    if (processAttributeField(fieldPath, pickConfig)) return;

    // Handle simple fields (default case)
    pickConfig.simpleFields.push(fieldPath);
  });

  // Expand dependencies and time-flexible fields before returning
  return expandPickConfigDependencies(pickConfig, dependencies, historicalPeriod);
};

/**
 * Determines if a refetch is needed based on column state changes
 * Only refetch when new columns are added, not when columns are removed
 * @param {Array} currentColumnState - Previous column state
 * @param {Array} newColumnState - New column state
 * @returns {boolean} - True if refetch is needed
 */
const shouldRefetchForColumnStateChange = (currentColumnState, newColumnState) => {
  if (!currentColumnState || !newColumnState) {
    return true;
  }

  // If both column states are the same reference, no need to refetch
  if (currentColumnState === newColumnState) {
    return false;
  }

  // Extract visible column IDs from both states
  const currentVisibleColumns = new Set(
    currentColumnState.filter(col => !col.hide).map(col => col.colId)
  );

  const newVisibleColumns = new Set(newColumnState.filter(col => !col.hide).map(col => col.colId));

  // Check if any new columns were added
  const addedColumns = [];
  newVisibleColumns.forEach(colId => {
    if (!currentVisibleColumns.has(colId)) {
      addedColumns.push(colId);
    }
  });

  return addedColumns.length > 0;
};

/**
 * Merges attributes from pickFields with existing displayedAttributes
 * @param {Array|Set} existingDisplayedAttributes - Existing displayedAttributes
 * @param {Object} pickFields - Pick configuration object
 * @returns {Array} Merged array of unique attribute keys
 */
const mergeAttributesFromPickFields = (existingDisplayedAttributes, pickFields) => {
  // Convert existing attributes to array if it's a Set
  const existingArray = Array.isArray(existingDisplayedAttributes)
    ? existingDisplayedAttributes
    : Array.from(existingDisplayedAttributes || []);

  if (!pickFields || !pickFields.simpleFields || !Array.isArray(pickFields.simpleFields)) {
    return existingArray;
  }

  const attributesFromPickFields = pickFields.simpleFields
    .filter(field => field.startsWith('attributes.'))
    .map(field => field.split('attributes.')[1]);

  // Merge and deduplicate
  const resSet = new Set([...existingArray, ...attributesFromPickFields]);
  return Array.from(resSet);
};

/**
 * Extracts and merges tsgCompetitors from pickFields with existing toolStoreGroupCompetitors
 * Formats competitors as "tsgKey::competitorKey" to match existing format
 * @param {Array} existingToolStoreGroupCompetitors - Existing toolStoreGroupCompetitors array
 * @param {Object} pickFields - Pick configuration object
 * @returns {Array} Merged array of toolStoreGroupCompetitors
 */
const mergeToolStoreGroupCompetitors = (existingToolStoreGroupCompetitors, pickFields) => {
  if (!pickFields || !pickFields.tsgCompetitors) {
    return existingToolStoreGroupCompetitors || [];
  }

  const existingSet = new Set(existingToolStoreGroupCompetitors || []);

  // Extract all competitor keys from all toolStoreGroups and format as "tsgKey::competitorKey"
  Object.entries(pickFields.tsgCompetitors).forEach(([tsgKey, competitorKeys]) => {
    if (Array.isArray(competitorKeys)) {
      competitorKeys.forEach(competitorKey => {
        const formattedCompetitor = `${tsgKey}::${competitorKey}`;
        existingSet.add(formattedCompetitor);
      });
    }
  });

  return Array.from(existingSet);
};

/**
 * Checks if a competitor column should be hidden based on selectedCompetitors and visibleCompetitors
 * @param {string} columnIdentifier - The column identifier (colId or field)
 * @param {Array} selectedCompetitors - Array of selected competitor objects with competitorKey
 * @param {Set} visibleCompetitors - Set of visible competitor keys (format: "competitorKey" or "tsgKey::competitorKey")
 * @returns {boolean} True if the column should be hidden, false otherwise
 */
const shouldHideCompetitorColumn = (
  columnIdentifier,
  selectedCompetitors,
  visibleCompetitors = new Set()
) => {
  if (!columnIdentifier || !columnIdentifier.includes('competitors.')) {
    return false; // Not a competitor column
  }

  // Extract competitor key from column identifier
  const parts = columnIdentifier.split('.');

  // Handle different column ID formats:
  // 1. Simple format: competitors.${competitorKey}.${fieldName}
  // 2. With tool store groups: toolStoreGroups.${tsgKey}.competitors.${competitorKey}.${fieldName}
  let competitorKey = null;
  let columnTsgKey = null;

  if (parts.length >= 3) {
    if (parts[0] === 'competitors') {
      // Simple format
      competitorKey = Number(parts[1]);
    } else if (parts[0] === 'toolStoreGroups' && parts.length >= 4) {
      // With tool store groups format
      columnTsgKey = parts[1];
      competitorKey = Number(parts[3]);
    }
  }

  if (competitorKey) {
    // Check if this competitor is in the selectedCompetitors (globally visible)
    const isGloballySelected = selectedCompetitors.some(
      comp => comp.competitorKey === competitorKey
    );

    if (isGloballySelected) {
      return false; // Always show if globally selected
    }

    // Check if this competitor is in the visibleCompetitors set
    let isLocallyVisible = false;

    if (columnTsgKey) {
      // For tool store group columns, check "tsgKey::competitorKey" format
      const tsgCompetitorKey = `${columnTsgKey}::${competitorKey}`;
      isLocallyVisible = visibleCompetitors.has(tsgCompetitorKey);
    } else {
      // For simple competitor columns, check just "competitorKey" format
      isLocallyVisible =
        visibleCompetitors.has(competitorKey) || visibleCompetitors.has(competitorKey.toString());
    }

    return !isLocallyVisible; // Hide if not locally visible
  }

  return false; // Default to not hiding if we can't determine
};

/**
 * Helper function to check if a column belongs to a tool store group that's completely hidden
 * @param {Object} column - The column object from column state
 * @param {Array} currentColumnState - The current column state array
 * @returns {boolean} - True if the column belongs to a hidden tool store group
 */
const isColumnInHiddenToolStoreGroup = (column, currentColumnState = []) => {
  const columnIdentifier = column.colId || column.field;

  // Only check competitor columns that belong to tool store groups
  if (!columnIdentifier || !columnIdentifier.includes('toolStoreGroups.')) {
    return false;
  }

  // Extract the tool store group key from the column identifier
  // Format: toolStoreGroups.${tsgKey}.competitors.${competitorKey}.${fieldName}
  const tsgKey = getTsgKeyFromColId(columnIdentifier);
  if (!tsgKey) {
    return false;
  }

  // Find the tool store group column in the current state
  const toolStoreGroupColumn = currentColumnState.find(col => {
    const colId = col.colId || col.field;
    return colId && colId.startsWith(`toolStoreGroups.${tsgKey}`);
  });

  // If the tool store group column is hidden, then all its child columns should not be modified
  return !!toolStoreGroupColumn && toolStoreGroupColumn.hide === true;
};

/**
 * Extracts visible tool store group keys from a layout's column state
 * @param {Array} columnState - The column state from a layout
 * @returns {Array} Array of tool store group keys that have visible columns
 */
const extractVisibleToolStoreGroupKeys = columnState => {
  if (!columnState || !Array.isArray(columnState)) {
    return [];
  }

  const visibleToolStoreGroupKeys = new Set();

  columnState.forEach(column => {
    const columnIdentifier = column.colId || column.field;

    if (columnIdentifier && columnIdentifier.includes('toolStoreGroups.')) {
      const tsgKey = getTsgKeyFromColId(columnIdentifier);

      if (tsgKey) {
        // Only include if the column is not hidden
        if (column.hide !== true) {
          visibleToolStoreGroupKeys.add(tsgKey);
        }
      }
    }
  });

  return Array.from(visibleToolStoreGroupKeys);
};

// Update the export
export default {
  defaultParser,
  getNumericColumnCustomType,
  validations,
  utils,
  getTruncatedValueGetters,
  getStrictWidthColumn,
  getNumericFilterParams,
  getMultiNumericFilterParams,
  getDashWhenEqualToRefFilterValueGetter,
  refreshGridCells,
  extractPickFieldsFromColumnState,
  shouldRefetchForColumnStateChange,
  fieldDependencies,
  expandFieldDependencies,
  expandPickConfigDependencies,
  expandTimeFlexibleFields,
  generateTimeFlexibleFieldNames,
  mergeAttributesFromPickFields,
  mergeToolStoreGroupCompetitors,
  shouldHideCompetitorColumn,
  isColumnInHiddenToolStoreGroup,
  extractVisibleToolStoreGroupKeys,
};
